<template>
  <div>
    <!-- Email Form -->
    <b-card>
      <b-row class="mb-4">
        <b-col class="col-md-12">
          <h2 class="text-blue font-weight-bold mt-1">
            {{ $t("send_admin_email") }}
          </h2>
        </b-col>
      </b-row>

      <b-form @submit.prevent="sendEmails">
        <!-- German Section -->
        <div class="mb-4">
          <b-row>
            <b-col>
              <h4 class="mb-3">{{ $t("german_section") }} (Deutsch)</h4>

              <b-form-group
                  :label="$t('subject') + ' (DE)'"
                  label-for="subject-de"
              >
                <b-form-input
                    id="subject-de"
                    v-model="form.subject_de"
                    :placeholder="$t('enter_subject_german')"
                    required
                ></b-form-input>
              </b-form-group>

              <b-form-group
                  :label="$t('content') + ' (DE)'"
                  label-for="content-de"
              >
                <b-form-textarea
                    id="content-de"
                    v-model="form.content_de"
                    :placeholder="$t('enter_content_german')"
                    rows="6"
                    required
                ></b-form-textarea>
              </b-form-group>

              <b-form-group
                  :label="$t('attachments') + ' (DE)'"
                  label-for="attachments-de"
              >
                <b-form-file
                    id="attachments-de"
                    v-model="form.attachments_de"
                    :placeholder="$t('choose_files')"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    multiple
                    @change="validateFiles($event, 'de')"
                ></b-form-file>
                <small class="text-muted">
                  {{ $t("max_2_files_5mb") }}
                </small>
              </b-form-group>

              <!-- File List DE -->
              <div v-if="form.attachments_de && form.attachments_de.length > 0" class="mt-2">
                <div v-for="(file, index) in form.attachments_de" :key="index" class="d-flex align-items-center mb-1">
                  <feather-icon icon="FileIcon" size="16" class="mr-1"/>
                  <span>{{ file.name }} ({{ formatFileSize(file.size) }})</span>
                </div>
              </div>
            </b-col>

            <b-col>
              <!-- English Section -->
              <h4 class="mb-3">{{ $t("english_section") }} (English)</h4>

              <b-form-group
                  :label="$t('subject') + ' (EN)'"
                  label-for="subject-en"
              >
                <b-form-input
                    id="subject-en"
                    v-model="form.subject_en"
                    :placeholder="$t('enter_subject_english')"
                    required
                ></b-form-input>
              </b-form-group>

              <b-form-group
                  :label="$t('content') + ' (EN)'"
                  label-for="content-en"
              >
                <b-form-textarea
                    id="content-en"
                    v-model="form.content_en"
                    :placeholder="$t('enter_content_english')"
                    rows="6"
                    required
                ></b-form-textarea>
              </b-form-group>

              <b-form-group
                  :label="$t('attachments') + ' (EN)'"
                  label-for="attachments-en"
              >
                <b-form-file
                    id="attachments-en"
                    v-model="form.attachments_en"
                    :placeholder="$t('choose_files')"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    multiple
                    @change="validateFiles($event, 'en')"
                ></b-form-file>
                <small class="text-muted">
                  {{ $t("max_2_files_5mb") }}
                </small>
              </b-form-group>

              <!-- File List EN -->
              <div v-if="form.attachments_en && form.attachments_en.length > 0" class="mt-2">
                <div v-for="(file, index) in form.attachments_en" :key="index" class="d-flex align-items-center mb-1">
                  <feather-icon icon="FileIcon" size="16" class="mr-1"/>
                  <span>{{ file.name }} ({{ formatFileSize(file.size) }})</span>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>

        <hr>

        <!-- Recipient Selection -->
        <div class="mb-4 recipient-section">
          <h4 class="mb-4 d-flex align-items-center">
            <feather-icon icon="UsersIcon" size="20" class="mr-1 text-primary"/>
            {{ $t("recipients") }}
          </h4>

          <b-row class="match-height">
            <!-- Left Column: Filters and Actions -->
            <b-col md="5" lg="4" class="d-flex flex-column">
              <EmailRecipientFilters
                  :selected-roles.sync="selectedRolesObjects"
                  :role-options="roleOptions"
                  :search-query.sync="searchQuery"
                  :user-count="users.length"
                  :selected-count="form.recipients.length"
                  @select-all="selectAllUsers"
                  @deselect-all="deselectAllUsers"
              />
            </b-col>

            <!-- Right Column: User Selection List -->
            <b-col md="7" lg="8" class="d-flex flex-column">
              <EmailUserList
                  :users="filteredUsers"
                  :selected-recipients.sync="form.recipients"
                  :filtered-users-count="filteredUsers.length"
                  :search-query="searchQuery"
                  :has-selected-roles="selectedRolesObjects.length > 0"
                  :loading="loading"
              />
            </b-col>
          </b-row>
        </div>

        <!-- Submit Button -->
        <div class="mt-4">
          <b-button
              type="submit"
              variant="primary"
              :disabled="loading || form.recipients.length === 0"
          >
            <b-spinner v-if="loading" small class="mr-1"></b-spinner>
            {{ loading ? $t("sending") : $t("send_email") }}
          </b-button>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormFile,
  BButton,
  BSpinner,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import EmailRecipientFilters from "./components/EmailRecipientFilters.vue";
import EmailUserList from "./components/EmailUserList.vue";

export default {
  name: "AdminEmailSender",

  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormFile,
    BButton,
    BSpinner,
    EmailRecipientFilters,
    EmailUserList,
  },

  data() {
    return {
      loading: false,
      selectedRolesObjects: [],
      searchQuery: "",
      users: [],
      form: {
        subject_de: "",
        content_de: "",
        subject_en: "",
        content_en: "",
        attachments_de: [],
        attachments_en: [],
        recipients: [],
      },
      roleOptions: [
        {value: "investors", text: this.$t("investors")},
        {value: "sellers", text: this.$t("sellers")},
        {value: "debtors", text: this.$t("debtors")},
        {value: "partners", text: this.$t("partners")},
        {value: "all", text: this.$t("all_users")},
      ],
    };
  },

  computed: {
    /**
     * Filter users based on search query
     */
    filteredUsers() {
      if (!this.searchQuery) return this.users;

      const query = this.searchQuery.toLowerCase();
      return this.users.filter(user =>
          user.full_name.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query)
      );
    },
  },

  watch: {
    selectedRolesObjects: {
      handler() {
        this.fetchUsers();
      },
      deep: true,
    },
  },

  methods: {
    /**
     * Fetch users based on selected role
     */
    async fetchUsers() {
      if (!this.selectedRolesObjects || this.selectedRolesObjects.length === 0) {
        this.users = [];
        this.form.recipients = [];
        return;
      }

      this.loading = true;

      try {
        const roles = this.selectedRolesObjects.map(role => role.value);
        const response = await this.$http.get("/admin/emails/users", {
          params: {roles},
        });

        if (response.data && response.data.value) {
          this.users = response.data.value;
          // Keep only recipients that are still in the new users list
          const newUserIds = this.users.map(u => Number(u.id)).filter(id => !isNaN(id));
          this.form.recipients = this.form.recipients.filter(id =>
              newUserIds.includes(Number(id))
          );
        } else {
          this.users = [];
          this.form.recipients = [];
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("error"),
            text: this.$t("failed_to_fetch_users"),
            icon: "AlertCircleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },

    /**
     * Validate uploaded files
     */
    validateFiles(event, lang) {
      const files = event.target.files || [];

      // Check max 2 files
      if (files.length > 2) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("error"),
            text: this.$t("max_2_files_allowed"),
            icon: "AlertCircleIcon",
            variant: "warning",
          },
        });
        if (lang === 'de') this.form.attachments_de = [];
        else this.form.attachments_en = [];
        return;
      }

      // Check file sizes (5MB max each)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      for (let file of files) {
        if (file.size > maxSize) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t("error"),
              text: `${file.name} ${this.$t("exceeds_5mb")}`,
              icon: "AlertCircleIcon",
              variant: "warning",
            },
          });
          if (lang === 'de') this.form.attachments_de = [];
          else this.form.attachments_en = [];
          return;
        }
      }
    },

    /**
     * Format file size for display
     */
    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
    },

    /**
     * Select all users
     */
    selectAllUsers() {
      this.form.recipients = this.users.map(user => user.id);
    },

    /**
     * Deselect all users
     */
    deselectAllUsers() {
      this.form.recipients = [];
    },

    /**
     * Toggle user selection on click
     */
    toggleUserSelection(userId) {
      const index = this.form.recipients.indexOf(userId);

      if (index > -1) {
        this.form.recipients.splice(index, 1);
      } else {
        this.form.recipients.push(userId);
      }
    },

    /**
     * Send bulk emails
     */
    async sendEmails() {
      // Validation
      if (!this.form.subject_de || !this.form.content_de) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("error"),
            text: this.$t("german_fields_required"),
            icon: "AlertCircleIcon",
            variant: "warning",
          },
        });
        return;
      }

      if (!this.form.subject_en || !this.form.content_en) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("error"),
            text: this.$t("english_fields_required"),
            icon: "AlertCircleIcon",
            variant: "warning",
          },
        });
        return;
      }

      if (this.form.recipients.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("error"),
            text: this.$t("select_at_least_one_recipient"),
            icon: "AlertCircleIcon",
            variant: "warning",
          },
        });
        return;
      }

      this.loading = true;

      try {
        // Prepare FormData for file upload
        const formData = new FormData();
        formData.append("subject_de", this.form.subject_de);
        formData.append("content_de", this.form.content_de);
        formData.append("subject_en", this.form.subject_en);
        formData.append("content_en", this.form.content_en);

        // Append roles
        this.selectedRolesObjects.forEach((role, index) => {
          formData.append(`recipient_roles[${index}]`, role.value);
        });

        // Append recipients with both the full ID (with prefix) and numeric ID
        this.form.recipients.forEach((recipientId, index) => {
          let numericId;
          let prefix = '';

          // Check if ID is a string with underscore format like 'u_3', 'd_4', etc.
          if (typeof recipientId === 'string' && recipientId.includes('_')) {
            const parts = recipientId.split('_');
            prefix = parts[0]; // Get the prefix (u, d, p, etc.)
            numericId = parseInt(parts[parts.length - 1]);
          } else {
            numericId = Number(recipientId);
          }

          if (!isNaN(numericId) && numericId > 0) {
            // Send the full prefixed ID
            formData.append(`recipients[${index}]`, recipientId);
            // Also send the type for backend reference
            formData.append(`recipient_types[${index}]`, prefix);
          }
        });

        // Append German attachments
        if (this.form.attachments_de && this.form.attachments_de.length > 0) {
          this.form.attachments_de.forEach((file, index) => {
            formData.append(`attachments_de[${index}]`, file);
          });
        }

        // Append English attachments
        if (this.form.attachments_en && this.form.attachments_en.length > 0) {
          this.form.attachments_en.forEach((file, index) => {
            formData.append(`attachments_en[${index}]`, file);
          });
        }

        const response = await this.$http.post("/admin/emails/send", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.data && response.data.status === 200) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t("success"),
              text: this.$t("emails_sent_successfully"),
              icon: "CheckCircleIcon",
              variant: "success",
            },
          });

          // Reset form
          this.resetForm();
        } else {
          throw new Error("Unexpected response");
        }
      } catch (error) {
        console.error("Error sending emails:", error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("error"),
            text: error.response?.data?.message || this.$t("failed_to_send_emails"),
            icon: "AlertCircleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },

    /**
     * Reset form to initial state
     */
    resetForm() {
      this.form = {
        subject_de: "",
        content_de: "",
        subject_en: "",
        content_en: "",
        attachments_de: [],
        attachments_en: [],
        recipients: [],
      };
      this.selectedRolesObjects = [];
      this.users = [];
    },
  },
};
</script>

<style scoped>
.text-blue {
  color: #345B48;
}

.recipient-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.match-height > [class*='col'] {
  display: flex;
  flex-flow: column;
}

.match-height > [class*='col'] > .card,
.match-height > [class*='col'] > .filter-card,
.match-height > [class*='col'] > .user-list-container {
  flex: 1 1 auto;
}
</style>
